/**
 * Create and edit a purchase order draft.
 *
 * TWO THINGS DIFFER FROM THE SALES-ORDER EDITOR, AND BOTH ARE BUSINESS FACTS
 * ---------------------------------------------------------------------------
 *  1. **The line rate is prefilled from the product's PURCHASE price**, not its
 *     sale price. The product model already carries `purchasePricePaise`, so the
 *     editor uses it and the user overtypes it freely. Choosing a product never
 *     WRITES a price back to the catalogue — a shop buys one item at different
 *     prices from different suppliers, and silently editing the catalogue on every
 *     order would destroy that.
 *  2. **Place of supply comes from the SUPPLIER's state.** The preview totals run
 *     the real calculation engine, so the number under the user's cursor is the
 *     number that will be stored — including IGST when the supplier is in another
 *     state. Previewing with the sales direction would show a total the saved
 *     document would never agree with.
 *
 * The totals are computed with `computeDocument`, the SAME engine the service
 * calls. A second calculation here would be a second answer to "what is this
 * order worth", and the two would drift.
 */
import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { asBasisPoints, asMoney, asQuantity } from '@core/money';
import { computeDocument } from '@core/calc';
import { financialYearFor } from '@domain/financialYear';
import { formatMoney } from '@core/money/format';
import { parseMoney, parseQuantity } from '@core/money';
import { asMoney as moneyValue } from '@core/money/types';
import { getDb } from '@data/db/database';
import { resolveActiveBusiness } from '@data/services/businessSession';
import { toast } from '@ui/components/Toaster';
import { DocumentScreen } from '@features/documents/DocumentScreen';
import {
  useCreatePurchaseOrder,
  useDeletePurchaseOrder,
  usePurchaseOrder,
  usePurchaseOrderOptions,
  useUpdatePurchaseOrder,
} from './usePurchaseOrders';
import type { PurchaseOrderLineInput } from '@data/services/purchaseOrderService';
import { STANDARD_UNITS, DEFAULT_UNIT } from '@features/masterData/units';

interface LineState {
  readonly id: string;
  readonly productId: string;
  readonly quantity: string;
  readonly rate: string;
  readonly description: string;
  readonly unit: string;
  readonly taxRateBp?: number;
  readonly cessRateBp?: number;
}
interface FormState {
  readonly supplierId: string;
  readonly docDate: string;
  readonly lines: readonly LineState[];
  readonly roundOff: boolean;
  readonly terms: string;
  readonly notes: string;
  readonly placeOfSupplyOverride: string;
}

let lineCounter = 0;
const nextLineId = () => `po-line-${++lineCounter}`;
const inputClass =
  'w-full rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100';
const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
};
const blankState = (): FormState => ({
  supplierId: '',
  docDate: today(),
  lines: [{ id: nextLineId(), productId: '', quantity: '1', rate: '', description: '', unit: 'NOS' }],
  roundOff: false,
  terms: '',
  notes: '',
  placeOfSupplyOverride: '',
});

function totalsFor(input: {
  business: NonNullable<Awaited<ReturnType<typeof resolveActiveBusiness>>>;
  supplier: { stateCode: string } | null;
  state: FormState;
  productsById: ReadonlyMap<string, { product: { name: string; unit: string; cessRateBp: number }; taxRateBp: number | null }>;
}) {
  const { business, supplier, state, productsById } = input;
  const lines = state.lines.filter((line) => line.productId && productsById.has(line.productId));
  if (!lines.length) return null;
  return computeDocument({
    business: { stateCode: business.stateCode, currency: 'INR' },
    docType: 'PURCHASE_ORDER',
    docDate: state.docDate,
    financialYear: financialYearFor(state.docDate),
    // THE PURCHASE DIRECTION. The supply originates at the SUPPLIER, so their
    // state decides IGST versus CGST+SGST — not the shop's own.
    placeOfSupplyCode: state.placeOfSupplyOverride || supplier?.stateCode || '',
    reverseCharge: false,
    lines: lines.map((line) => {
      const product = productsById.get(line.productId);
      const quantity = Number(line.quantity);
      const rate = Number(line.rate);
      return {
        productId: line.productId,
        description: line.description.trim() || product?.product.name || 'Item',
        unit: product?.product.unit ?? 'NOS',
        quantity: asQuantity(Math.round((Number.isFinite(quantity) ? quantity : 0) * 1000)),
        freeQuantity: asQuantity(0),
        ratePaise: asMoney(Math.round((Number.isFinite(rate) ? rate : 0) * 100)),
        discountType: 'NONE' as const,
        discountValue: 0,
        taxRateBp: asBasisPoints(line.taxRateBp ?? product?.taxRateBp ?? 0),
        cessRateBp: asBasisPoints(line.cessRateBp ?? product?.product.cessRateBp ?? 0),
      };
    }),
    charges: [],
    invoiceDiscount: null,
    roundOff: state.roundOff,
    roundOffMode: business.documentSettings.roundOffMode,
    tcs: null,
  });
}

function toServiceLines(lines: readonly LineState[]): PurchaseOrderLineInput[] {
  return lines.map((line) => ({
    productId: line.productId,
    quantityMilli: parseQuantity(line.quantity, 3),
    ratePaise: parseMoney(line.rate),
    unit: line.unit,
    ...(line.description.trim() ? { description: line.description.trim() } : {}),
  }));
}

export function PurchaseOrderEditorScreen({ documentId = null }: { documentId?: string | null }) {
  const options = usePurchaseOrderOptions();
  const existing = usePurchaseOrder(documentId);
  const create = useCreatePurchaseOrder();
  const update = useUpdatePurchaseOrder();
  const remove = useDeletePurchaseOrder();
  const [business, setBusiness] = useState<Awaited<ReturnType<typeof resolveActiveBusiness>> | null>(
    null,
  );
  const [state, setState] = useState<FormState>(blankState);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  useEffect(() => {
    void resolveActiveBusiness(getDb() as never).then(setBusiness).catch(() => setBusiness(null));
  }, []);

  const products = useMemo(() => options.data?.products ?? [], [options.data?.products]);
  const suppliers = options.data?.suppliers ?? [];
  const productsById = useMemo(
    () => new Map(products.map((entry) => [entry.product.id, entry])),
    [products],
  );

  // SEEDING DURING RENDER, GUARDED — the pattern the sales-order editor uses. An
  // effect would re-run on every refetch and overwrite what the user is typing.
  if (documentId && existing.data && existing.data.stored.status === 'DRAFT' && loadedId !== documentId) {
    setState({
      supplierId: existing.data.stored.supplierId ?? '',
      docDate: existing.data.stored.docDate,
      lines: existing.data.lines.map((line) => {
        const product = productsById.get(line.productId ?? '');
        return {
          id: nextLineId(),
          productId: line.productId ?? '',
          quantity: String(line.quantityMilli / 1000),
          // Paise back to RUPEES, because this is a text field.
          rate: String(line.ratePaise / 100),
          description: line.description,
          unit: line.unit ?? product?.product.unit ?? DEFAULT_UNIT,
          taxRateBp: line.taxRateBp,
          cessRateBp: line.cessRateBp,
        };
      }),
      roundOff: existing.data.stored.roundOffPaise !== 0,
      terms: existing.data.stored.terms ?? '',
      notes: existing.data.stored.notes ?? '',
      placeOfSupplyOverride: existing.data.stored.placeOfSupplyCode,
    });
    setLoadedId(documentId);
  }

  const editing = documentId !== null;
  // An ISSUED order is immutable, so the editor is not offered at all. Falling
  // through to the document screen teaches the rule by showing the record, rather
  // than by refusing a save.
  if (editing && existing.data && existing.data.stored.status !== 'DRAFT') {
    return <DocumentScreen documentId={documentId} />;
  }
  const supplier = suppliers.find((entry) => entry.id === state.supplierId) ?? null;
  const totals = business ? totalsFor({ business, supplier, state, productsById }) : null;

  async function save() {
    const valid =
      state.docDate &&
      supplier &&
      state.lines.length > 0 &&
      state.lines.every((line) => {
        try {
          return (
            line.productId !== '' &&
            Number(
              parseQuantity(line.quantity, productsById.get(line.productId)?.product.unitPrecision ?? 3),
            ) > 0 &&
            Number(parseMoney(line.rate)) >= 0
          );
        } catch {
          // `parseMoney` and `parseQuantity` both THROW on "abc". Called straight
          // from a keystroke handler they would blank the screen, so an unparseable
          // field simply makes the form unsaveable.
          return false;
        }
      });
    if (!business || !valid) {
      toast({ title: 'Complete the purchase order details', tone: 'danger' });
      return;
    }
    try {
      const payload = {
        supplierId: state.supplierId,
        docDate: state.docDate,
        lines: toServiceLines(state.lines),
        roundOff: state.roundOff,
        terms: state.terms,
        notes: state.notes,
        placeOfSupplyOverride: state.placeOfSupplyOverride,
      };
      const result =
        editing && documentId
          ? await update.mutateAsync({ id: documentId, input: payload })
          : await create.mutateAsync({ ...payload, businessId: business.id });
      toast({ title: `Purchase order ${result.docNumber} saved as draft`, tone: 'success' });
      // Create navigates to the edit route; an EDIT stays put, because
      // re-navigating would re-seed the form and could discard a line being typed.
      if (!editing) window.location.hash = `#/purchases/orders/${result.id}/edit`;
    } catch (error) {
      toast({
        title: 'Could not save purchase order',
        description: error instanceof Error ? error.message : 'Try again.',
        tone: 'danger',
      });
    }
  }

  async function deleteDraft() {
    if (!documentId || !window.confirm('Delete this purchase order draft?')) return;
    try {
      await remove.mutateAsync(documentId);
      toast({ title: 'Purchase order draft deleted', tone: 'success' });
      window.location.hash = '#/purchases/orders';
    } catch (error) {
      toast({
        title: 'Could not delete purchase order',
        description: error instanceof Error ? error.message : 'Try again.',
        tone: 'danger',
      });
    }
  }

  if (options.isLoading || (editing && existing.isLoading) || !business) {
    return <p className="p-4 text-sm text-slate-400">Loading purchase order…</p>;
  }

  return <div className="flex flex-col gap-4 p-4 text-slate-100">
    <header>
      <h1 className="text-lg font-semibold">{editing ? 'Edit purchase order draft' : 'New purchase order'}</h1>
      <p className="text-xs text-slate-400">
        An order says what we intend to buy. It does not receive stock and does not create a
        supplier payable — both happen when goods are received.
      </p>
    </header>
    {editing && existing.data && (
      <p data-testid="purchase-order-number" className="text-sm">{existing.data.stored.docNumber}</p>
    )}
    {!suppliers.length || !products.length ? (
      <p className="text-sm text-slate-400">
        Add a supplier and at least one product before creating a purchase order. Suppliers are
        managed under Contacts → Suppliers.
      </p>
    ) : (
      <div className="grid gap-4 xl:grid-cols-[1fr_19rem]">
        <section className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-slate-400">Supplier
              <select
                data-testid="purchase-order-supplier"
                className={inputClass}
                value={state.supplierId}
                onChange={(event) => setState((old) => ({ ...old, supplierId: event.target.value }))}
              >
                <option value="">Choose supplier…</option>
                {suppliers.map((entry) => (
                  <option key={entry.id} value={entry.id}>{entry.name}</option>
                ))}
              </select>
            </label>
            <label className="text-xs text-slate-400">Date
              <input
                data-testid="purchase-order-date"
                type="date"
                className={inputClass}
                value={state.docDate}
                onChange={(event) => setState((old) => ({ ...old, docDate: event.target.value }))}
              />
            </label>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-400">
                <tr><th>Product / description</th><th>Qty</th><th>Unit</th><th>Rate</th><th></th></tr>
              </thead>
              <tbody>
                {state.lines.map((line) => (
                  <tr key={line.id} className="border-t border-slate-800">
                    <td className="py-2 pr-2">
                      <select
                        data-testid="purchase-order-product"
                        className={inputClass}
                        value={line.productId}
                        onChange={(event) => {
                          const product = productsById.get(event.target.value);
                          setState((old) => ({
                            ...old,
                            lines: old.lines.map((item) =>
                              item.id === line.id
                                ? {
                                    ...item,
                                    productId: event.target.value,
                                    // Prefilled from the PURCHASE price, never the sale
                                    // price, and never written back to the catalogue —
                                    // one item costs different suppliers different amounts.
                                    rate: product
                                      ? String((product.product.purchasePricePaise ?? 0) / 100)
                                      : '',
                                    unit: product?.product.unit ?? 'NOS',
                                    taxRateBp: undefined,
                                    cessRateBp: undefined,
                                  }
                                : item,
                            ),
                          }));
                        }}
                      >
                        <option value="">Choose product…</option>
                        {products.map((entry) => (
                          <option key={entry.product.id} value={entry.product.id}>{entry.product.name}</option>
                        ))}
                      </select>
                      <input
                        className={`${inputClass} mt-1`}
                        aria-label="Purchase order line description"
                        placeholder="Description"
                        value={line.description}
                        onChange={(event) =>
                          setState((old) => ({
                            ...old,
                            lines: old.lines.map((item) =>
                              item.id === line.id ? { ...item, description: event.target.value } : item,
                            ),
                          }))
                        }
                      />
                    </td>
                    <td>
                      <input
                        data-testid="purchase-order-quantity"
                        className={inputClass}
                        inputMode="decimal"
                        value={line.quantity}
                        onChange={(event) =>
                          setState((old) => ({
                            ...old,
                            lines: old.lines.map((item) =>
                              item.id === line.id ? { ...item, quantity: event.target.value } : item,
                            ),
                          }))
                        }
                      />
                    </td>
                    <td>
                      <select
                        data-testid="purchase-order-unit"
                        className={inputClass}
                        value={line.unit}
                        onChange={(event) =>
                          setState((old) => ({
                            ...old,
                            lines: old.lines.map((item) =>
                              item.id === line.id ? { ...item, unit: event.target.value } : item,
                            ),
                          }))
                        }
                      >
                        {STANDARD_UNITS.map((u) => (
                          <option key={u.code} value={u.code}>
                            {u.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        data-testid="purchase-order-rate"
                        className={inputClass}
                        inputMode="decimal"
                        value={line.rate}
                        onChange={(event) =>
                          setState((old) => ({
                            ...old,
                            lines: old.lines.map((item) =>
                              item.id === line.id ? { ...item, rate: event.target.value } : item,
                            ),
                          }))
                        }
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        aria-label="Remove purchase order line"
                        onClick={() =>
                          setState((old) => ({
                            ...old,
                            lines: old.lines.filter((item) => item.id !== line.id),
                          }))
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button
            type="button"
            className="rounded border border-slate-700 px-3 py-2 text-sm"
            onClick={() =>
              setState((old) => ({
                ...old,
                lines: [...old.lines, { id: nextLineId(), productId: '', quantity: '1', rate: '', description: '', unit: 'NOS' }],
              }))
            }
          >
            <Plus className="mr-1 inline h-4 w-4" />Add line
          </button>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={state.roundOff}
              onChange={(event) => setState((old) => ({ ...old, roundOff: event.target.checked }))}
            />
            Round off total
          </label>
          <label className="block text-xs text-slate-400">Terms
            <textarea
              data-testid="purchase-order-terms"
              className={`${inputClass} mt-1`}
              rows={3}
              value={state.terms}
              onChange={(event) => setState((old) => ({ ...old, terms: event.target.value }))}
            />
          </label>
          <label className="block text-xs text-slate-400">Notes
            <textarea
              data-testid="purchase-order-notes"
              className={`${inputClass} mt-1`}
              rows={3}
              value={state.notes}
              onChange={(event) => setState((old) => ({ ...old, notes: event.target.value }))}
            />
          </label>
        </section>
        <aside className="h-fit rounded border border-slate-800 p-4">
          <h2 className="mb-3 text-xs uppercase text-slate-400">Purchase order total</h2>
          <div className="flex justify-between border-t border-slate-700 py-2 font-semibold">
            <span>Total</span>
            <span data-testid="purchase-order-total">
              {totals ? formatMoney(moneyValue(totals.grandTotalPaise)) : '—'}
            </span>
          </div>
          {supplier?.stateCode && !state.placeOfSupplyOverride && (
            <p className="mb-2 text-xs text-slate-500">
              Tax is split using the supplier&apos;s state ({supplier.stateCode}), not this
              shop&apos;s.
            </p>
          )}
          {!supplier?.stateCode && !state.placeOfSupplyOverride && (
            <p className="mb-2 text-xs text-amber-400/80">
              This supplier has no state on file, so the tax split is unresolved. Set one on the
              supplier rather than guessing here.
            </p>
          )}
          <button
            type="button"
            data-testid="purchase-order-save"
            disabled={!totals || create.isPending || update.isPending}
            onClick={() => void save()}
            className="mt-3 w-full rounded bg-brand-500 px-3 py-2 text-sm font-medium disabled:opacity-40"
          >
            {editing ? 'Save changes' : 'Save draft'}
          </button>
          {editing && documentId && (
            <>
              <a
                className="mt-2 block rounded border border-slate-700 px-3 py-2 text-center text-sm"
                href={`#/purchases/orders/${documentId}`}
              >
                Preview without saving
              </a>
              <button
                type="button"
                data-testid="delete-purchase-order-draft"
                className="mt-2 w-full rounded border border-red-900 px-3 py-2 text-sm text-red-300"
                disabled={remove.isPending}
                onClick={() => void deleteDraft()}
              >
                Delete draft
              </button>
            </>
          )}
          <a className="mt-2 block text-center text-sm text-slate-400" href="#/purchases/orders">
            Back to purchase orders
          </a>
        </aside>
      </div>
    )}
  </div>;
}